import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Coins, Zap, X, ExternalLink, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Top-up modal — opens when user clicks the WalletBalanceBadge or when the
 * ClaudeFAB intercepts a 402 insufficient_credits response from /api/ai/chat.
 *
 * Lists token packs, opens Stripe Checkout on click. Stripe webhook credits
 * the wallet server-side; on return to the app the badge auto-refreshes via
 * the `wallet:refresh` event.
 */
export default function TopUpModal({ open, onOpenChange, lowBalanceReason }) {
  const [packs, setPacks] = useState([]);
  const [explanation, setExplanation] = useState('');
  const [trialBonus, setTrialBonus] = useState(3.0);
  const [loadingPack, setLoadingPack] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/wallet/packs`);
        if (!r.ok) return;
        const d = await r.json();
        setPacks(d.packs || []);
        setExplanation(d.explanation || '');
        setTrialBonus(d.trial_signup_bonus_usd || 3.0);
      } catch {
        /* noop */
      }
    })();
  }, [open]);

  const handleBuy = async (packId) => {
    setLoadingPack(packId);
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${API_URL}/api/wallet/topup/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ pack: packId, origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) { window.alert(`Checkout failed: ${d.detail || r.statusText}`); return; }
      window.location.href = d.url;
    } catch (e) {
      window.alert(`Checkout failed: ${e.message}`);
    } finally {
      setLoadingPack(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-cyan-500/30 max-w-2xl" data-testid="topup-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cyan-300">
            <Coins className="w-5 h-5" />
            AI Copilot Token Packs
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            {lowBalanceReason ? (
              <span className="block mb-2 text-amber-300">⚠ {lowBalanceReason}</span>
            ) : null}
            Top up your AI Copilot balance. Used only for Claude API calls. Your tunes,
            datalogs, and tune-builder buttons keep working without credits.
          </DialogDescription>
        </DialogHeader>

        {explanation && (
          <div className="text-xs text-zinc-400 bg-zinc-900 border border-zinc-800 rounded-sm p-3" data-testid="topup-explanation">
            {explanation}
            <div className="mt-2 text-fuchsia-300">
              New trial accounts get <strong>${trialBonus.toFixed(2)} in AI credits free</strong> on signup.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {packs.map((p) => {
            const isLoading = loadingPack === p.id;
            const isBest = p.id === 'pro';
            return (
              <button
                key={p.id}
                type="button"
                disabled={isLoading}
                onClick={() => handleBuy(p.id)}
                className={`text-left p-4 rounded-sm border transition-colors ${
                  isBest
                    ? 'bg-cyan-500/10 border-cyan-500/40 hover:bg-cyan-500/20'
                    : 'bg-zinc-900 border-zinc-700 hover:bg-zinc-800 hover:border-zinc-600'
                }`}
                data-testid={`topup-pack-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-zinc-100 uppercase tracking-wider font-['JetBrains_Mono']">{p.name}</span>
                      {isBest && <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/30 text-cyan-200 rounded-sm font-bold">BEST VALUE</span>}
                    </div>
                    <div className="text-[11px] text-zinc-400 mt-0.5">{p.tagline}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-cyan-300 font-['JetBrains_Mono']">${p.price_usd.toFixed(0)}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1.5 text-[11px] text-cyan-400 font-['JetBrains_Mono']">
                  {isLoading ? (
                    <><Loader2 className="w-3 h-3 animate-spin" />redirecting…</>
                  ) : (
                    <><Zap className="w-3 h-3" />Buy via Stripe <ExternalLink className="w-3 h-3" /></>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="text-[11px] text-zinc-500 mt-2">
          Powered by Stripe Checkout. Credits land in your wallet within seconds after payment confirms.
          Beta testers and trial users — see your balance badge for current pool status.
        </div>
      </DialogContent>
    </Dialog>
  );
}
